import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

type HistoryState = Record<string, unknown> | null;

/** Scroll writes are batched: Safari throttles frequent `history.replaceState`. */
const SAVE_DELAY_MS = 150;

/**
 * Keeps the `scrollTop` of `scrollRef` on the current browser history entry
 * under `key`. Returning to that entry (back button, swipe back, reload)
 * restores the position once `ready` turns true — i.e. when the content that
 * gives the container its height is rendered. A fresh navigation (new entry,
 * nothing stored) leaves the container at the top.
 */
export function useHistoryScrollPosition(
  scrollRef: RefObject<HTMLElement>,
  key: string,
  ready: boolean,
): void {
  const restoredRef = useRef(false);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!ready || restoredRef.current || !element) return;
    restoredRef.current = true;

    const stored = (window.history.state as HistoryState)?.[key];
    if (typeof stored !== 'number' || stored <= 0) return;
    element.scrollTop = stored;
  }, [key, ready, scrollRef]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;

    let timer: number | undefined;
    const write = () => {
      window.clearTimeout(timer);
      const state = window.history.state as HistoryState;
      if (state?.[key] === element.scrollTop) return;
      window.history.replaceState({ ...state, [key]: element.scrollTop }, '');
    };
    const scheduleWrite = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(write, SAVE_DELAY_MS);
    };

    element.addEventListener('scroll', scheduleWrite, { passive: true });
    // A tap on a card navigates away right after a scroll — write before the
    // router pushes the next entry.
    element.addEventListener('click', write, true);
    return () => {
      // A pending write would land on the next page's history entry.
      window.clearTimeout(timer);
      element.removeEventListener('scroll', scheduleWrite);
      element.removeEventListener('click', write, true);
    };
  }, [key, scrollRef]);
}
